import { useEffect, useRef } from 'react';

/**
 * Fire `onSelect(text)` when the user finishes selecting text inside `ref`.
 *
 * WHY NOT onMouseUp
 *   Highlighting was wired to onMouseUp alone. On iPadOS that event never comes
 *   for a text selection: the long-press and the handle drags are handled by
 *   the OS selection UI, not delivered to the element. The selection itself
 *   works — students could see the blue handles — but nothing downstream ever
 *   ran, so the highlight never appeared and the feature looked broken on
 *   exactly the device the study uses.
 *
 *   `selectionchange` is the one signal both mouse and touch produce. It fires
 *   continuously while the handles move, so the commit is debounced: the
 *   selection is taken once it has stopped changing, which is also when the
 *   student has finished choosing.
 */
export function useSelectionCapture(ref, onSelect, { enabled = true, settleMs = 350 } = {}) {
  const cbRef = useRef(onSelect);
  cbRef.current = onSelect;

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return undefined;
    let timer = null;

    const settle = () => {
      timer = null;
      const node = ref && ref.current;
      if (!node) return;
      const selection = window.getSelection && window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;

      // Only claim selections that live inside THIS surface — the same handler
      // shape is mounted on several messages at once.
      const range = selection.getRangeAt(0);
      if (!node.contains(range.commonAncestorContainer)) return;

      const text = selection.toString().trim();
      if (!text) return;
      cbRef.current(text, selection);
    };

    const onChange = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(settle, settleMs);
    };

    document.addEventListener('selectionchange', onChange);
    return () => {
      document.removeEventListener('selectionchange', onChange);
      if (timer) clearTimeout(timer);
    };
  }, [ref, enabled, settleMs]);
}

export default useSelectionCapture;
